import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ViewMode } from 'src/enums';
import * as acBase from 'src/store/states/GenericActionCreator';
import { AdditionalServicesData, CheckedWeOwe, RecallData, CheckedRecall, CheckedRecom, DoneElsewhere, MCData } from 'src/models';
import { IStoreState } from 'src/store';
import { AxiosError } from 'axios';

export interface IServicesState {
    allServices: AdditionalServicesData;
    checkedWeOwes: CheckedWeOwe[];
    recallData: RecallData;
    maintContractData: MCData;
    checkedRecalls: CheckedRecall[];
    checkedRecoms: CheckedRecom[];
    doneElsewhere: DoneElsewhere[];
}

export const initialAdditionalServices: AdditionalServicesData = {
    weOwes: [],
    recom: [],
    inquiryInvoked: false,
    built: false,
    returnCode: '',
};

export const suppressedAdditionalServices: AdditionalServicesData = {
    weOwes: [],
    recom: [],
    inquiryInvoked: false,
    built: true,
    returnCode: '',
};

export const initialRecallData: RecallData = {
    recalls: [],
    inquiryReturned: false,
    inquiryError: false,
};

export const initialMaintContractsData: MCData = {
    maintContracts: [],
    TMNAMileage: 0,
    MCReturned: false,
    MCError: false,
    returnCode: '',
};

/** Initial State */
export const initialState: IServicesState = {
    allServices: initialAdditionalServices,
    checkedWeOwes: [],
    recallData: initialRecallData,
    maintContractData: initialMaintContractsData,
    checkedRecalls: [],
    checkedRecoms: [],
    doneElsewhere: [],
};

export const AdditionalServicesSlice = createSlice({
    name: 'AdditionalServices',
    initialState: initialState,
    reducers: {
        receiveAdditionalServices: (state, action: PayloadAction<IServicesState>) => {
            state.allServices = action.payload.allServices;
            state.checkedWeOwes = action.payload.checkedWeOwes;
            state.checkedRecalls = action.payload.checkedRecalls;
            state.checkedRecoms = action.payload.checkedRecoms;
            state.doneElsewhere = action.payload.doneElsewhere;
        },
        suppressAdditionalServices: (state) => {
            state.allServices.built = true;
            state.checkedWeOwes = [];
            state.checkedRecalls = [];
            state.checkedRecoms = [];
        },
        clearServices: (state, action: PayloadAction<AdditionalServicesData>) => {
            state.allServices = action.payload;
            state.checkedWeOwes = [];
            state.checkedRecalls = [];
            state.checkedRecoms = [];
            state.doneElsewhere = [];
            state.recallData = initialRecallData;
            // state.maintContractData = initialMaintContractsData;
        },
        clearMaintContractData: (state) => {
            state.maintContractData = initialMaintContractsData;
        },
        getRecalls: (state, action: PayloadAction<RecallData>) => {
            state.recallData = action.payload;
        },
        getMaintContracts: (state, action: PayloadAction<MCData>) => {
            state.maintContractData = action.payload;
        },
        toggleWeOwe: (state, action: PayloadAction<CheckedWeOwe[]>) => {
            state.checkedWeOwes = action.payload;
        },
        toggleRecall: (state, action: PayloadAction<CheckedRecall[]>) => {
            state.checkedRecalls = action.payload;
        },
        toggleRecommended: (state, action: PayloadAction<CheckedRecom[]>) => {
            state.checkedRecoms = action.payload;
        },
        toggleDoneElsewhere: (state, action: PayloadAction<DoneElsewhere[]>) => {
            state.doneElsewhere = action.payload;
        },
        clearChecks: (state) => {
            state.checkedWeOwes = [];
            state.checkedRecalls = [];
            state.checkedRecoms = [];
            state.doneElsewhere = [];
        },
    },
});

export const {
    receiveAdditionalServices,
    suppressAdditionalServices,
    clearServices,
    getRecalls,
    getMaintContracts,
    toggleWeOwe,
    toggleRecall,
    toggleRecommended,
    toggleDoneElsewhere,
    clearChecks,
    clearMaintContractData,
} = AdditionalServicesSlice.actions;

export default AdditionalServicesSlice.reducer;

export function GetAdditionalServices(profileNum: string, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const apptVehVin = getState().Appointment.ApptVeh.vin;
        const logId = getState().Specs.specs?.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        const selectedVehicle = getState().Vehicles.selectedVehicle;

        let vinOwned = getState().Vehicles.decodedData?.decodeErr;
        if (vinOwned) {
            vinOwned = vinOwned?.split(':')[0];
        }
        if (apptVehVin) {
            const apptCustLastName = getState().Appointment.ApptCust.custLastName;
            const apptCustEmail = getState().Appointment.ApptCust.custEmailAddress;
            const apptVehMileage = getState().Appointment.ApptVeh.mileage;
            const reqData = {
                keyVin: apptVehVin,
                keyLastName: apptCustLastName,
                keyEmail: apptCustEmail,
                keyMileage: apptVehMileage,
                keyProfile: profileNum,
                logId: logId,
                vinOwned: vinOwned,
                nameId: selectedVehicle,
                callingProgram: callingProgram,
            };
            const response = await acBase.eraWebServiceCall<AdditionalServicesData | AxiosError>(
                'SVPController', 'AdditionalServices', reqData, store, branch);

            if (acBase.handleAxiosError(response, dispatch, { operation: 'AdditionalServices' })) {
                return;
            }
            const newState: IServicesState = {
                allServices: response,
                checkedRecalls: [...getState().AdditionalServices.checkedRecalls],
                checkedRecoms: [...getState().AdditionalServices.checkedRecoms],
                checkedWeOwes: [...getState().AdditionalServices.checkedWeOwes],
                recallData: initialRecallData,
                doneElsewhere: [...getState().AdditionalServices.doneElsewhere],
                maintContractData: initialMaintContractsData,
            };
            dispatch(receiveAdditionalServices(newState));
        } else {
            dispatch(suppressAdditionalServices());
        }
    };
}

export function GetRecalls() {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        const apptVehVin = getState().Appointment.ApptVeh.vin;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const profile = getState().Framework.specProfile;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        let nameId = getState().Customer.Customer.nameId;
        const selectedVehicle = getState().Vehicles.selectedVehicle;
        const vehicle = selectedVehicle ? getState().Vehicles.vehicles[parseInt(selectedVehicle)] : null;
        if (vehicle) {
            nameId = vehicle.nameId;
        }
        const reqData = {
            keyVin: apptVehVin,
            keyProfile: profile,
            logId: logId,
            nameId: nameId,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<RecallData | AxiosError>(
            'SVPController', 'GetRecalls', reqData, store, branch);

        if (acBase.handleAxiosError(response, dispatch, { operation: 'GetRecalls' })) {
            return;
        }
        dispatch(getRecalls(response));
    };
}

export function ToggleWeOwe(desc) {
    return (dispatch, getState: () => IStoreState) => {
        let checkedWeOwes = [...getState().AdditionalServices.checkedWeOwes];
        const alreadyChecked = checkedWeOwes.findIndex(result => result.weOweDesc === desc);
        if (alreadyChecked === -1) {
            checkedWeOwes.push({ weOweDesc: desc });
        } else {
            checkedWeOwes = checkedWeOwes.filter(result => result.weOweDesc !== desc);
        }
        dispatch(toggleWeOwe(checkedWeOwes));
    };
}

export function ToggleRecall(number) {
    return (dispatch, getState: () => IStoreState) => {
        let checkedRecalls = [...getState().AdditionalServices.checkedRecalls];
        const alreadyChecked = checkedRecalls.findIndex(result => result.campaignNum === number);
        if (alreadyChecked === -1) {
            checkedRecalls.push({ campaignNum: number });
        } else {
            checkedRecalls = checkedRecalls.filter(result => result.campaignNum !== number);
        }
        dispatch(toggleRecall(checkedRecalls));
    };
}

export function ToggleRecommended(code) {
    return (dispatch, getState: () => IStoreState) => {
        let checkedRecoms = [...getState().AdditionalServices.checkedRecoms];
        const alreadyChecked = checkedRecoms.findIndex(result => result.opCode === code);
        if (alreadyChecked === -1) {
            checkedRecoms.push({ opCode: code });
        } else {
            checkedRecoms = checkedRecoms.filter(result => result.opCode !== code);
        }
        dispatch(toggleRecommended(checkedRecoms));
    };
}

export function ToggleDoneElsewhere(code) {
    return (dispatch, getState: () => IStoreState) => {
        let doneElsewhere = [...getState().AdditionalServices.doneElsewhere];
        const alreadyChecked = doneElsewhere.findIndex(result => result.opCode === code);
        if (alreadyChecked === -1) {
            doneElsewhere.push({ opCode: code });
        } else {
            doneElsewhere = doneElsewhere.filter(result => result.opCode !== code);
        }
        dispatch(toggleDoneElsewhere(doneElsewhere));
    };
}

export function ClearAdditionalServices() {
    return (dispatch) => {
        dispatch(clearServices(initialAdditionalServices));
    };
}

export function ClearCheckedAdditionalServices() {
    return (dispatch) => {
        dispatch(clearChecks());
    };
}

export function ClearMaintContractData() {
    return (dispatch) => {
        dispatch(clearMaintContractData());
    };
}

export function GetMaintContracts() {
    // return (dispatch) => {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        const apptVehVin = getState().Appointment.ApptVeh.vin;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const profile = getState().Framework.specProfile;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        let nameId = getState().Customer.Customer.nameId;
        const selectedVehicle = getState().Vehicles.selectedVehicle;
        const vehicle = selectedVehicle ? getState().Vehicles.vehicles[parseInt(selectedVehicle)] : null;
        if (vehicle) {
            nameId = vehicle.nameId;
        }
        const reqData = {
            keyVin: apptVehVin,
            keyProfile: profile,
            logId: logId,
            nameId: nameId,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<MCData | AxiosError>(
            'SVPController', 'GetMaintContracts', reqData, store, branch);

        if (acBase.handleAxiosError(response, dispatch, { operation: 'GetMaintContracts' })) {
            return;
        }
        dispatch(getMaintContracts(response));
    };
}